import type { CatalogueActionKind } from "@reminder-tracker/backend-contracts";
import type {
  CatalogueDateMeaning,
  CatalogueTypeId,
  CompletionTypeBinding,
} from "@reminder-tracker/domain";

/** GF-020 / GF-022: these actions finish once; no recurrence or approval is inferred. */
export const everydayCompletionBindings = (
  ["personal-task", "personal-appointment", "personal-payment-date"] as const
).map((typeId) => ({
  typeId,
  profile: { profileId: "one-off-action" as const, version: 1, optionalFields: [] },
})) satisfies ReadonlyArray<CompletionTypeBinding>;

type Semantics = Readonly<{
  serviceTypeId: CatalogueTypeId;
  actionKind: CatalogueActionKind;
  dateKind: CatalogueDateMeaning["kind"];
}>;
const meanings = {
  choose: { serviceTypeId: "personal-task", actionKind: "task", dateKind: "chosen-task-date" },
  due: { serviceTypeId: "personal-task", actionKind: "task", dateKind: "confirmed-task-date" },
  update: { serviceTypeId: "personal-task", actionKind: "update", dateKind: "chosen-review-date" },
  review: { serviceTypeId: "personal-task", actionKind: "review", dateKind: "chosen-review-date" },
  appointment: {
    serviceTypeId: "personal-appointment",
    actionKind: "appointment",
    dateKind: "appointment-or-planning-date",
  },
  payment: {
    serviceTypeId: "personal-payment-date",
    actionKind: "payment-date",
    dateKind: "bill-or-notice-date",
  },
  licence: { serviceTypeId: "personal-task", actionKind: "renewal", dateKind: "actual-expiry" },
  petCover: {
    serviceTypeId: "pet-cover-renewal",
    actionKind: "renewal",
    dateKind: "actual-renewal",
  },
  drivingAddress: {
    serviceTypeId: "driver-record-address-update",
    actionKind: "update",
    dateKind: "chosen-review-date",
  },
  vehicleAddress: {
    serviceTypeId: "vehicle-logbook-address-update",
    actionKind: "update",
    dateKind: "chosen-review-date",
  },
  nationalAddress: {
    serviceTypeId: "national-address-update",
    actionKind: "update",
    dateKind: "chosen-review-date",
  },
  electoral: {
    serviceTypeId: "voter-registration-update",
    actionKind: "update",
    dateKind: "chosen-review-date",
  },
} as const satisfies Readonly<Record<string, Semantics>>;

/** Reviewed per member, never inferred from a title, market, or prototype source label. */
const assignments = {
  "move:notice": "due",
  "move:removals": "appointment",
  "move:redirect": "choose",
  "move:cover": "choose",
  "move:old-energy": "choose",
  "move:old-water": "choose",
  "move:old-internet": "choose",
  "move:final-energy": "choose",
  "move:final-water": "choose",
  "move:keys": "appointment",
  "move:new-energy": "choose",
  "move:new-water": "choose",
  "move:new-internet": "appointment",
  "move:inventory": "due",
  "move:bank": "update",
  "move:employer": "update",
  "move:motor-cover": "update",
  "move:school": "update",
  "move:pet-details": "update",
  "move:local-tax": "choose",
  "move:driving": "drivingAddress",
  "move:v5c": "vehicleAddress",
  "move:voter": "electoral",
  "move:tv": "update",
  "move:hmrc": "update",
  "move:benefits": "update",
  "move:deposit": "due",
  "move:return-router": "due",
  "move:tenancy": "choose",
  "move:official-address": "review",
  "move:national-address": "nationalAddress",
  "move:aadhaar": "update",
  "move:vehicle-address": "update",
  "move:services": "update",
  "move:licence": "review",
  "move:vehicle": "review",
  "move:hdb-keys": "appointment",
  "move:hdb-inspect": "due",
  "move:dewa": "choose",
  "holiday:documents": "review",
  "holiday:document-apply": "due",
  "holiday:balance": "payment",
  "holiday:insurance": "choose",
  "holiday:checkin": "due",
  "holiday:pet-care": "choose",
  "holiday:parking": "payment",
  "holiday:return-vehicle": "appointment",
  "pet:vaccination": "due",
  "pet:flea": "due",
  "pet:worm": "due",
  "pet:medicine": "due",
  "pet:groom": "appointment",
  "pet:insurance": "petCover",
  "pet:chip-update": "update",
  "pet:registration-apply": "choose",
  "pet:licence-renew": "licence",
  "pet:requirements": "due",
  "pet:boarding": "appointment",
  "pet:collect": "appointment",
  "baby:leave-request": "due",
  "baby:support-apply": "due",
  "baby:return-work": "due",
  "baby:register": "due",
  "baby:certificate": "choose",
  "baby:health-registration": "due",
  "baby:passport": "choose",
  "baby:appointment": "appointment",
  "baby:place": "due",
  "vehicle-buy:payment": "payment",
  "vehicle-buy:cover": "choose",
  "vehicle-buy:collection": "appointment",
  "vehicle-buy:complete-transfer": "due",
  "vehicle-buy:registration": "due",
  "vehicle-buy:parking": "update",
  "vehicle-buy:toll": "update",
  "vehicle-buy:vehicle-tax": "choose",
  "job:notice": "due",
  "job:return": "due",
  "job:expenses": "due",
  "job:forms": "due",
  "job:benefits": "due",
  "job:p45": "due",
  "job:permit": "due",
  "job:work-pass": "due",
  "job:transfer": "due",
  "job:uan": "due",
  "job:payroll": "due",
  "job:coverage-choice": "due",
  "study:offer": "due",
  "study:enrol": "due",
  "study:deposit": "payment",
  "study:funding": "due",
  "study:offer-home": "due",
  "study:deposit-home": "payment",
  "study:keys": "appointment",
  "school:apply": "due",
  "school:offer": "due",
  "school:forms": "due",
  "school:deposit": "payment",
  "school:induction": "appointment",
  "school:fees": "payment",
  "marriage:appointment": "appointment",
  "marriage:documents": "due",
  "marriage:venue-deposit": "payment",
  "marriage:numbers": "due",
  "marriage:balance": "payment",
  "marriage:returns": "due",
  "marriage:certificate": "choose",
} as const satisfies Readonly<Record<string, keyof typeof meanings>>;

export const everydayBundleMemberKeys = Object.keys(assignments);

export function everydayBundleMemberSemantics(key: string): Semantics {
  if (!Object.prototype.hasOwnProperty.call(assignments, key))
    throw new Error(`Bundle member has no reviewed action mapping: ${key}`);
  return meanings[assignments[key as keyof typeof assignments]];
}
